"use client";

import { useCallback } from "react";
import { useTheme } from "@/features/theme";

interface RouteRangeSliderProps {
  startFrac: number;   // 0–1
  endFrac: number;     // 0–1
  totalDistanceM: number;
  onChangeStart: (v: number) => void;
  onChangeEnd: (v: number) => void;
  isDark: boolean;
}

function fmtKm(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

export default function RouteRangeSlider({
  startFrac,
  endFrac,
  totalDistanceM,
  onChangeStart,
  onChangeEnd,
  isDark: isDarkProp,
}: RouteRangeSliderProps) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  const handleStart = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Math.min(parseFloat(e.target.value), endFrac - 0.01);
    onChangeStart(Math.max(0, v));
  }, [endFrac, onChangeStart]);

  const handleEnd = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Math.max(parseFloat(e.target.value), startFrac + 0.01);
    onChangeEnd(Math.min(1, v));
  }, [startFrac, onChangeEnd]);

  const trackColor = isDark ? "#3f3f46" : "#e4e4e7"; // zinc-700 / zinc-200
  const fillColor  = "#6366f1"; // indigo-500

  const gradient = `linear-gradient(to right, ${trackColor} ${startFrac * 100}%, ${fillColor} ${startFrac * 100}%, ${fillColor} ${endFrac * 100}%, ${trackColor} ${endFrac * 100}%)`;

  const labelClass ="text-tone-400";

  // When both thumbs are near each other, the start thumb needs higher z-index
  // so the user can drag it leftward (away from end). Otherwise end is on top.
  // Also when start is at max (> 0.95), bring it on top so it's reachable.
  const startZ = startFrac > endFrac - 0.05 || startFrac > 0.95 ? 5 : 3;
  const endZ   = startZ === 5 ? 4 : 5;

  return (
    <div className="space-y-2">
      {/* Distance labels */}
      <div className="flex justify-between text-[10px] font-mono">
        <span className={labelClass}>{fmtKm(startFrac * totalDistanceM)}</span>
        <span className={`font-semibold ${isDark ? "text-indigo-400" : "text-indigo-600"}`}>
          {fmtKm((endFrac - startFrac) * totalDistanceM)}
        </span>
        <span className={labelClass}>{fmtKm(endFrac * totalDistanceM)}</span>
      </div>

      {/* Dual-thumb slider track */}
      <div className="relative h-5 flex items-center">
        {/* Visible track */}
        <div
          className="absolute left-0 right-0 h-1.5 rounded-full pointer-events-none"
          style={{ background: gradient }}
        />

        {/* Start thumb */}
        <input
          type="range"
          min={0} max={1} step={0.001}
          value={startFrac}
          onChange={handleStart}
          className="range-thumb absolute w-full h-full opacity-0 cursor-pointer"
          style={{ zIndex: startZ }}
        />

        {/* End thumb */}
        <input
          type="range"
          min={0} max={1} step={0.001}
          value={endFrac}
          onChange={handleEnd}
          className="range-thumb absolute w-full h-full opacity-0 cursor-pointer"
          style={{ zIndex: endZ }}
        />

        {/* Visible thumb circles */}
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-indigo-500 bg-white shadow pointer-events-none"
          style={{ left: `calc(${startFrac * 100}% - 8px)`, zIndex: 6 }}
        />
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-indigo-500 bg-white shadow pointer-events-none"
          style={{ left: `calc(${endFrac * 100}% - 8px)`, zIndex: 6 }}
        />
      </div>

      {/* Reset link */}
      {(startFrac > 0 || endFrac < 1) && (
        <button
          onClick={() => { onChangeStart(0); onChangeEnd(1); }}
          className={`text-[10px] underline underline-offset-2 text-tone-500 hover:text-tone-300`}
        >
          Reset trim
        </button>
      )}
    </div>
  );
}
